/**
 * Bootstrap. Constructs the pieces, hands them to the session, mounts the UI.
 * No business logic lives here (CODE_RULES.md §3).
 */
import "./styles/index.css";

import { logger } from "./core/logger";
import { describeUnknown } from "./core/result";
import { mount, requireElement } from "./ui/components/dom";
import { createAppShell } from "./ui/app-shell";
import { createUploadPanel } from "./ui/panels/upload-panel";
import { createPdfPanel } from "./ui/panels/pdf-panel";
import { createToggleList } from "./ui/panels/toggle-list";
import { createPropertiesPanel } from "./ui/panels/properties-panel";
import { createViewer } from "./viewer/world";
import { initFragments } from "./viewer/fragments-engine";
import { createSelection } from "./viewer/selection";
import { createSession, type Session } from "./session";

async function start(): Promise<void> {
  const host = requireElement("app");

  const upload = createUploadPanel();
  const pdf = createPdfPanel();
  const properties = createPropertiesPanel();

  // Panels are built before the session exists, so they reach it through this
  // holder. It is assigned exactly once, immediately below.
  let session: Session | null = null;

  const shell = createAppShell(host, {
    onModeChange: (mode) => session?.setMode(mode),
    onPlanSelect: (id) => session?.openPlan(id),
    onResetVisibility: () => session?.resetVisibility(),
  });

  const storeys = createToggleList("Storeys", (id, on) => {
    void session?.setVisibility("storey", id, on);
  });
  const categories = createToggleList("Layers", (id, on) => {
    void session?.setVisibility("category", id, on);
  });

  const viewer = await createViewer(shell.viewport);
  const fragments = initFragments(viewer.components, viewer.world);

  session = createSession(viewer, { shell, upload, pdf, storeys, categories });

  const selection = createSelection(viewer.components, viewer.world, fragments);
  selection.on("selected", (element) => {
    properties.show(element);
  });
  selection.on("cleared", () => {
    properties.showEmpty();
  });

  mount(shell.sidebar, storeys.root, categories.root, properties.root);
  mount(shell.overlay, pdf.root, upload.root);

  const active = session;
  upload.onFiles((files) => {
    void active.handleFiles(files);
  });

  session.setMode("3d");
  logger.info("Viewer ready");
}

start().catch((cause: unknown) => {
  logger.error("Failed to start viewer", cause);
  const host = document.getElementById("app");
  if (host !== null) {
    host.textContent = `Could not start the viewer: ${describeUnknown(cause)}`;
  }
});
